/**
 * CoffeeMates Auth Service — OTP via Phone (SMS) or Email
 */

import httpClient from '../utils/httpClient';

export interface TokenResponse {
  access_token: string;
  token_type: string;
  user_id: string;
  is_new_user: boolean;
}

export interface UserProfile {
  id: string;
  phone: string;
  email: string | null;
  first_name: string;
  last_name: string;
  father_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  industry: string | null;
  role: string;
  status: string;
  reputation_score: number;
  events_attended: number;
  wallet_balance: number;
  safety_acknowledged: boolean;
  referral_code: string | null;
  user_tier: string;
  interests: string[];
  onboarding_completed: boolean;
  created_at: string;
  // Profile/gamification fields surfaced by the backend `users` table
  // (see backend/models/user.py).
  skills?: string[];
  looking_for?: string | null;
  user_level?: 'explorer' | 'regular' | 'connector' | 'ambassador' | string;
  current_streak?: number;
  achievements?: Array<{ id: string; emoji?: string; title?: string; earned_at?: string }>;
}

export interface RegisterData {
  first_name: string;
  last_name: string;
  father_name?: string;
  email?: string;
  phone?: string;
}

const authService = {
  /** Request OTP via phone */
  async requestOTP(phone: string) {
    const { data } = await httpClient.post('/auth/otp/request', { phone });
    return data;
  },

  /** Request OTP via email */
  async requestOTPByEmail(email: string) {
    const { data } = await httpClient.post('/auth/otp/request', { email });
    return data;
  },

  /** Verify OTP sent to phone */
  async verifyOTP(phone: string, code: string): Promise<TokenResponse> {
    const { data } = await httpClient.post<TokenResponse>('/auth/otp/verify', { phone, code });
    localStorage.setItem('cm_token', data.access_token);
    localStorage.setItem('cm_user_id', data.user_id);
    return data;
  },

  /** Verify OTP sent to email */
  async verifyOTPByEmail(email: string, code: string): Promise<TokenResponse> {
    const { data } = await httpClient.post<TokenResponse>('/auth/otp/verify', { email, code });
    localStorage.setItem('cm_token', data.access_token);
    localStorage.setItem('cm_user_id', data.user_id);
    return data;
  },

  async register(regData: RegisterData) {
    const { data } = await httpClient.post('/auth/register', regData);
    return data;
  },

  async getProfile(): Promise<UserProfile> {
    const { data } = await httpClient.get<UserProfile>('/auth/me');
    localStorage.setItem('cm_user', JSON.stringify(data));
    return data;
  },

  async refreshToken() {
    const { data } = await httpClient.post('/auth/refresh');
    if (data.access_token) {
      localStorage.setItem('cm_token', data.access_token);
    }
    return data;
  },

  logout() {
    localStorage.removeItem('cm_token');
    localStorage.removeItem('cm_user');
    localStorage.removeItem('cm_user_id');
  },

  getStoredToken(): string | null {
    return localStorage.getItem('cm_token');
  },

  getStoredUser(): UserProfile | null {
    const raw = localStorage.getItem('cm_user');
    return raw ? JSON.parse(raw) : null;
  },

  isAuthenticated(): boolean {
    return !!localStorage.getItem('cm_token');
  },
};

export default authService;
